import React from 'react';
import { Users, Activity, Settings, LogOut, ShieldCheck, Sparkles, ChevronRight, FolderKanban } from 'lucide-react';
import { useAuth } from '../../AuthProvider';

export type AdminTab = 'activity' | 'topup' | 'user' | 'docs' | 'setting' | 'users' | 'library' | 'config';

interface SidebarProps {
  activeAdminTab: AdminTab;
  setActiveAdminTab: (tab: AdminTab) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeAdminTab,
  setActiveAdminTab,
}) => {
  const { userProfile, logout } = useAuth();

  // Normalize active tab for matching
  const normalizedActiveTab = 
    activeAdminTab === 'users' ? 'user' :
    activeAdminTab === 'library' ? 'docs' :
    activeAdminTab === 'config' ? 'setting' :
    activeAdminTab;

  const menuItems = [
    {
      id: 'activity' as AdminTab,
      label: 'Hoạt động Quản trị',
      path: '/admin',
      icon: Activity,
      description: 'Danh sách Admin & Nhật ký logs',
    },
    {
      id: 'topup' as AdminTab,
      label: 'Quản lý Thanh toán',
      path: '/admin/topup',
      icon: Sparkles,
      description: 'Đơn nạp, nâng VIP & duyệt tiền',
      badge: 'Cần duyệt'
    },
    {
      id: 'user' as AdminTab,
      label: 'Quản lý người dùng',
      path: '/admin/user',
      icon: Users,
      description: 'Danh sách, phân quyền & ví Xu',
    },
    {
      id: 'docs' as AdminTab,
      label: 'Thư viện Tài liệu',
      path: '/admin/docs',
      icon: FolderKanban,
      description: 'Google Drive Sync & Đặt giá Xu',
    },
    {
      id: 'setting' as AdminTab,
      label: 'Cấu hình Ứng dụng',
      path: '/admin/setting',
      icon: Settings,
      description: 'Bảo mật AES & Key hệ thống',
    },
  ];

  return (
    <aside className="w-full md:w-64 bg-white border border-slate-200/80 rounded-2xl p-4 shadow-xs flex flex-col justify-between shrink-0 space-y-6">
      <div className="space-y-5">
        {/* Admin Header Title */}
        <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-100 px-2">
          <div className="w-9 h-9 rounded-xl bg-[#224783] text-white flex items-center justify-center font-bold shadow-xs">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-extrabold text-slate-900 leading-tight">
              Quản Trị Hệ Thống
            </h2>
            <span className="text-[10px] font-bold text-[#224783] bg-[#224783]/10 px-2 py-0.5 rounded-full inline-block mt-0.5">
              Admin Portal v2.5
            </span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav className="space-y-1.5">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = normalizedActiveTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveAdminTab(item.id)}
                className={`w-full p-3 rounded-xl transition-all text-left flex items-center justify-between group cursor-pointer ${
                  isActive
                    ? 'bg-[#224783] text-white shadow-sm font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-[#224783]'}`} />
                  <div className="min-w-0">
                    <div className="text-xs font-bold truncate leading-tight flex items-center space-x-1.5">
                      <span>{item.label}</span>
                      {item.badge && !isActive && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-md font-bold">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className={`text-[10px] truncate mt-0.5 ${isActive ? 'text-blue-100' : 'text-slate-400'}`}>
                      {item.description}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${isActive ? 'text-white opacity-100' : 'text-slate-300 opacity-0 group-hover:opacity-100'}`} />
              </button>
            );
          })}
        </nav>
      </div>

      {/* Admin Profile Footer & Logout */}
      <div className="pt-4 border-t border-slate-100 space-y-3">
        <div className="flex items-center space-x-3 px-2">
          <div className="w-9 h-9 rounded-xl bg-slate-200 border border-slate-300 overflow-hidden shrink-0">
            {userProfile?.photoURL ? (
              <img src={userProfile.photoURL} alt="Admin Avatar" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-bold text-slate-500 text-xs">
                {userProfile?.displayName?.slice(0, 2)?.toUpperCase() || 'AD'}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-slate-900 truncate">
              {userProfile?.displayName || 'Quản trị viên'}
            </div>
            <div className="text-[11px] text-slate-400 truncate">
              {userProfile?.email || 'admin@nhagiaoso.com'}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={logout}
          className="w-full py-2 px-3 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors flex items-center justify-center space-x-2 border border-rose-100"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span>Đăng xuất Quản trị</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
